/**
 * Payment Method Selector Component
 * Allows members to select and manage payment methods for receiving payouts
 * Trust Marketplace - Payment Account Management
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';
import {
  CreditCard,
  Plus,
  CheckCircle,
  AlertCircle,
  Building2,
  DollarSign,
  Trash2,
  Star
} from 'lucide-react';

interface PaymentMethodSelectorProps {
  memberId: number;
  selectedMethodId?: number;
  onSelectMethod: (methodId: number | null) => void;
  label?: string;
  showIssueCard?: boolean;
  onIssueCard?: () => void;
}

interface PaymentMethod {
  id: number;
  financial_institution_id?: number;
  institution_name?: string;
  account_type: string;
  account_number_last4?: string;
  routing_number?: string;
  card_network?: string;
  card_exp_month?: number;
  card_exp_year?: number;
  cardholder_name?: string;
  is_primary: boolean;
  payout_preference: string;
  verification_status: string;
  daily_payout_limit?: number;
  monthly_payout_limit?: number;
  is_active: boolean;
  created_at: string;
}

const ACCOUNT_TYPES = [
  { value: 'BANK_ACCOUNT', label: 'Bank Account (ACH)', icon: Building2 },
  { value: 'DEBIT_CARD', label: 'Debit Card', icon: CreditCard },
  { value: 'PREPAID_CARD', label: 'Prepaid Card', icon: CreditCard },
  { value: 'VIRTUAL_CARD', label: 'Virtual Card', icon: CreditCard }
];

const CARD_NETWORKS = ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER'];

export const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  memberId,
  selectedMethodId,
  onSelectMethod,
  label,
  showIssueCard = false,
  onIssueCard
}) => {
  const { t } = useAppTranslation();
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [adding, setAdding] = useState(false);

  const [newMethod, setNewMethod] = useState({
    account_type: 'BANK_ACCOUNT',
    account_number_encrypted: '',
    routing_number: '',
    card_network: 'VISA',
    card_exp_month: new Date().getMonth() + 1,
    card_exp_year: new Date().getFullYear() + 5,
    cardholder_name: '',
    payout_preference: 'ACH'
  });

  useEffect(() => {
    fetchMethods();
  }, [memberId]);

  const fetchMethods = async () => {
    try {
      setLoading(true);
      const response = await authFetch(getWeaveinApiUrl(`/api/members/${memberId}/payment-accounts?is_active=true`));

      if (response.ok) {
        const data = await response.json();
        setMethods(data);

        // Auto-select primary method if none selected
        if (!selectedMethodId && data.length > 0) {
          const primary = data.find((method: PaymentMethod) => method.is_primary);
          if (primary) {
            onSelectMethod(primary.id);
          } else {
            onSelectMethod(data[0].id);
          }
        }
      } else {
        throw new Error('Failed to fetch payment methods');
      }
    } catch (err) {
      console.error('Error fetching payment methods:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch methods');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMethod = async () => {
    try {
      setAdding(true);

      // Validate based on account type
      if (newMethod.account_type === 'BANK_ACCOUNT') {
        if (!newMethod.routing_number || !newMethod.account_number_encrypted) {
          alert('Please provide routing and account numbers');
          return;
        }
      } else {
        if (!newMethod.cardholder_name || !newMethod.account_number_encrypted) {
          alert('Please provide cardholder name and card number');
          return;
        }
      }

      // Extract last 4 digits
      const last4 = newMethod.account_number_encrypted.slice(-4);

      const response = await authFetch(getWeaveinApiUrl(`/api/members/${memberId}/payment-accounts`), {
        method: 'POST',
        jsonBody: {
            member_id: memberId,
            account_type: newMethod.account_type,
            account_number_encrypted: newMethod.account_number_encrypted, // TODO: Encrypt
            account_number_last4: last4,
            routing_number: newMethod.account_type === 'BANK_ACCOUNT' ? newMethod.routing_number : null,
            card_network: newMethod.account_type !== 'BANK_ACCOUNT' ? newMethod.card_network : null,
            card_exp_month: newMethod.account_type !== 'BANK_ACCOUNT' ? newMethod.card_exp_month : null,
            card_exp_year: newMethod.account_type !== 'BANK_ACCOUNT' ? newMethod.card_exp_year : null,
            cardholder_name: newMethod.account_type !== 'BANK_ACCOUNT' ? newMethod.cardholder_name : null,
            payout_preference: newMethod.payout_preference
          }
      });

      if (response.ok) {
        const created = await response.json();
        await fetchMethods();
        setShowAddDialog(false);
        resetForm();

        // Auto-select the newly added method
        onSelectMethod(created.id);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add payment method');
      }
    } catch (err) {
      console.error('Error adding payment method:', err);
      alert(err instanceof Error ? err.message : 'Failed to add method');
    } finally {
      setAdding(false);
    }
  };

  const resetForm = () => {
    setNewMethod({
      account_type: 'BANK_ACCOUNT',
      account_number_encrypted: '',
      routing_number: '',
      card_network: 'VISA',
      card_exp_month: new Date().getMonth() + 1,
      card_exp_year: new Date().getFullYear() + 5,
      cardholder_name: '',
      payout_preference: 'ACH'
    });
  };

  const getMethodIcon = (accountType: string) => {
    const typeInfo = ACCOUNT_TYPES.find(at => at.value === accountType);
    const Icon = typeInfo?.icon || CreditCard;
    return <Icon className="w-4 h-4" />;
  };

  const getMethodLabel = (method: PaymentMethod) => {
    if (method.account_type === 'BANK_ACCOUNT') {
      return `Bank Account (****${method.account_number_last4})`;
    } else {
      return `${method.card_network} ${method.account_type.replace('_', ' ')} (****${method.account_number_last4})`;
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="text-red-600 text-sm">
        <AlertCircle className="inline w-4 h-4 mr-1" />
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{label || 'Payment Method'}</Label>
        <div className="flex gap-2">
          {showIssueCard && onIssueCard && (
            <Button
              variant="outline"
              size="sm"
              onClick={onIssueCard}
              className="flex items-center gap-1"
            >
              <CreditCard className="w-3 h-3" />
              {t('financial.issue_card', 'Issue Card')}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddDialog(true)}
            className="flex items-center gap-1"
          >
            <Plus className="w-3 h-3" />
            {t('financial.add_method', 'Add Method')}
          </Button>
        </div>
      </div>

      {methods.length === 0 ? (
        <Card className="p-4 text-center">
          <DollarSign className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600 mb-2">{t('financial.no_payment_methods_configured', 'No payment methods configured')}</p>
          <Button onClick={() => setShowAddDialog(true)} size="sm">
            <Plus className="w-4 h-4 mr-1" />
            {t('financial.add_payment_method', 'Add Payment Method')}
          </Button>
        </Card>
      ) : (
        <Select
          value={selectedMethodId?.toString()}
          onValueChange={(value) => onSelectMethod(value ? parseInt(value) : null)}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('financial.select_payment_method', 'Select payment method...')} />
          </SelectTrigger>
          <SelectContent>
            {methods.map((method) => (
              <SelectItem key={method.id} value={method.id.toString()}>
                <div className="flex items-center gap-2">
                  {getMethodIcon(method.account_type)}
                  <span>{getMethodLabel(method)}</span>
                  {method.is_primary && (
                    <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                  )}
                  {method.verification_status === 'VERIFIED' ? (
                    <CheckCircle className="w-3 h-3 text-green-600" />
                  ) : (
                    <AlertCircle className="w-3 h-3 text-yellow-600" />
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {selectedMethodId && methods.find(m => m.id === selectedMethodId) && (
        <div className="text-xs text-gray-600 flex items-center gap-2 mt-1">
          {(() => {
            const selected = methods.find(m => m.id === selectedMethodId);
            return (
              <>
                {selected?.verification_status === 'VERIFIED' ? (
                  <span className="text-green-600">✓ Verified</span>
                ) : (
                  <span className="text-yellow-600">⚠ Pending verification</span>
                )}
                <span>•</span>
                <span>Preference: {selected?.payout_preference}</span>
              </>
            );
          })()}
        </div>
      )}

      {/* Add Payment Method Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('financial.add_payment_method_1', 'Add Payment Method')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="account_type">{t('financial.payment_method_type', 'Payment Method Type *')}</Label>
              <Select
                value={newMethod.account_type}
                onValueChange={(value) => setNewMethod({ ...newMethod, account_type: value })}
              >
                <SelectTrigger id="account_type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACCOUNT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {newMethod.account_type === 'BANK_ACCOUNT' ? (
              <>
                <div>
                  <Label htmlFor="routing_number">{t('financial.routing_number', 'Routing Number *')}</Label>
                  <Input
                    id="routing_number"
                    value={newMethod.routing_number}
                    onChange={(e) => setNewMethod({ ...newMethod, routing_number: e.target.value })}
                    placeholder="9-digit routing number"
                    maxLength={9}
                  />
                </div>

                <div>
                  <Label htmlFor="account_number">{t('financial.account_number', 'Account Number *')}</Label>
                  <Input
                    id="account_number"
                    type="password"
                    value={newMethod.account_number_encrypted}
                    onChange={(e) => setNewMethod({ ...newMethod, account_number_encrypted: e.target.value })}
                    placeholder={t('financial.account_number_1', 'Account number')}
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label htmlFor="cardholder_name">{t('financial.cardholder_name', 'Cardholder Name *')}</Label>
                  <Input
                    id="cardholder_name"
                    value={newMethod.cardholder_name}
                    onChange={(e) => setNewMethod({ ...newMethod, cardholder_name: e.target.value })}
                    placeholder={t('financial.name_on_card', 'Name on card')}
                  />
                </div>

                <div>
                  <Label htmlFor="card_number">{t('financial.card_number', 'Card Number *')}</Label>
                  <Input
                    id="card_number"
                    type="password"
                    value={newMethod.account_number_encrypted}
                    onChange={(e) => setNewMethod({ ...newMethod, account_number_encrypted: e.target.value })}
                    placeholder="16-digit card number"
                    maxLength={16}
                  />
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label htmlFor="card_network">{t('financial.network', 'Network')}</Label>
                    <Select
                      value={newMethod.card_network}
                      onValueChange={(value) => setNewMethod({ ...newMethod, card_network: value })}
                    >
                      <SelectTrigger id="card_network">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CARD_NETWORKS.map((network) => (
                          <SelectItem key={network} value={network}>
                            {network}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="exp_month">{t('financial.exp_month', 'Exp Month')}</Label>
                    <Input
                      id="exp_month"
                      type="number"
                      min="1"
                      max="12"
                      value={newMethod.card_exp_month}
                      onChange={(e) => setNewMethod({ ...newMethod, card_exp_month: parseInt(e.target.value) })}
                    />
                  </div>

                  <div>
                    <Label htmlFor="exp_year">{t('financial.exp_year', 'Exp Year')}</Label>
                    <Input
                      id="exp_year"
                      type="number"
                      value={newMethod.card_exp_year}
                      onChange={(e) => setNewMethod({ ...newMethod, card_exp_year: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <Label htmlFor="payout_preference">{t('financial.payout_preference', 'Payout Preference')}</Label>
              <Select
                value={newMethod.payout_preference}
                onValueChange={(value) => setNewMethod({ ...newMethod, payout_preference: value })}
              >
                <SelectTrigger id="payout_preference">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACH">{t('financial.ach_23_days', 'ACH (2-3 days)')}</SelectItem>
                  <SelectItem value="CARD">{t('financial.card_instant', 'Card (Instant)')}</SelectItem>
                  <SelectItem value="CHECK">{t('financial.check_57_days', 'Check (5-7 days)')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <p className="text-xs text-amber-600">
              ⚠️ In production, sensitive data will be encrypted before transmission
            </p>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }}>
                {t('financial.cancel', 'Cancel')}
              </Button>
              <Button onClick={handleAddMethod} disabled={adding}>
                {adding ? 'Adding...' : 'Add Method'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentMethodSelector;
